// Room furnishing (B6, monorail-n9v). Deterministic per-seed container
// placement: every room *can* hold a couple of breakables along its rim,
// treasure pockets always hold a few (the pocket keeps paying a little
// after its one-time orb is claimed — a visible promise must never read
// as a lie), gated vaults get one for flavor. Pure function of
// (layout, seed): the same save always finds the same furniture in the
// same corners, which is what makes the web learnable as a place.
//
// This module only decides WHERE things stand; what a container looks
// like and what falls out of it live in systems/containers.ts.

import { mulberry32, floatIn, intIn } from '../rng'
import { roomRadius } from './embed'
import { layoutSdf } from './carve'
import { T } from '../tunables'
import type { DungeonLayout } from './types'

export interface ContainerSpot {
  /** Stable per-seed index — the session broken-set keys on this. */
  id: number
  /** Room node the container furnishes. */
  node: number
  x: number
  y: number
}

export function furnishContainers(layout: DungeonLayout, seed: number): ContainerSpot[] {
  const c = T.containers
  const rng = mulberry32((seed ^ 0x85ebca6b) >>> 0)
  const spots: ContainerSpot[] = []

  for (const node of layout.graph.nodes) {
    let count = 0
    if (node.role === 'treasure') {
      count = c.treasureCount
    } else if (node.role === 'reward') {
      count = 1
    } else if (node.role === 'entrance') {
      // One by the hub shoreline: the safe classroom where breaking is
      // taught (predicted onboarding gap — nothing else explains the verb).
      count = 1
    } else {
      // Plain rooms: draw the count even when the room stays bare so the
      // stream position (and thus every later room) is seed-stable.
      const roll = rng()
      count = intIn(rng, 1, c.roomMax)
      if (roll >= c.roomChance) count = 0
    }

    const center = layout.pos[node.id]
    const rimR = roomRadius(node.sizeHint)
    for (let k = 0; k < count; k++) {
      // Rim placement: furniture stands against the banks, off the swim
      // lines. Rejection-sample into water with clearance for the body.
      for (let attempt = 0; attempt < 8; attempt++) {
        const ang = rng() * Math.PI * 2
        const dist = rimR * floatIn(rng, c.rimFrac[0], c.rimFrac[1])
        const x = center.x + Math.cos(ang) * dist
        const y = center.y + Math.sin(ang) * dist
        if (layoutSdf(layout, x, y) < -(c.radius + 0.08)) {
          spots.push({ id: spots.length, node: node.id, x, y })
          break
        }
      }
    }
  }
  return spots
}
